import { chakra } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Icon } from '../icons/Icon';
import { Button } from '../primitives/Button';
import { Heading } from '../primitives/Heading';
import { Text } from '../primitives/Text';

const Center = chakra('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2',
    maxWidth: 'content.narrow',
    mx: 'auto',
    py: '12',
    px: '6',
    textAlign: 'center',
  },
});

export interface ErrorStateProps {
  title?: ReactNode;
  description?: ReactNode;
  /** When provided, renders a "Try again" button that calls this. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Custom action(s), replacing the default retry button. */
  action?: ReactNode;
}

/** The canonical failure surface, with an optional retry affordance. */
export function ErrorState({
  title = 'Something went wrong',
  description,
  onRetry,
  retryLabel = 'Try again',
  action,
}: ErrorStateProps) {
  return (
    <Center role="alert">
      <chakra.div
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize="12"
        borderRadius="full"
        bg="status.dangerSubtle"
        mb="2"
      >
        <Icon as={AlertTriangle} size="lg" tone="danger" />
      </chakra.div>
      <Heading level={2} size="md">
        {title}
      </Heading>
      {description && (
        <Text tone="muted" size="md">
          {description}
        </Text>
      )}
      {action ?? (
        onRetry && (
          <chakra.div mt="4">
            <Button variant="secondary" onClick={onRetry}>
              {retryLabel}
            </Button>
          </chakra.div>
        )
      )}
    </Center>
  );
}
